<template>
  <div>
    <!-- SECTION EXPOSITION (quand homePage = false) -->
    <div v-if="!homePage" class="exhibition-wrapper">

      <!-- Header diagonal avec la première image en fond -->
      <section class="diagonal-header" :style="heroBackgroundStyle">
        <div class="header-overlay">
          <h1 class="expo-title">{{ name }}</h1>
        </div>
      </section>

      <!-- Zone “Collage” des ressources -->
      <section class="collage-section">
        <div
          v-for="(resource, index) in resources"
          :key="index"
          class="collage-item"
          :style="getRandomTransformStyle(index)"
          @click="openLightbox(index)"
        >
          <img
            :src="resourceUrls[index]"
            alt="Resource Image"
            class="collage-image"
          />
          <div class="collage-info">
            <h3 class="collage-title">{{ resource.title }}</h3>
            <p class="collage-description">{{ resource.description }}</p>
          </div>
        </div>
      </section>

      <!-- Footer incurvé -->
      <footer class="curved-footer">
        <div class="footer-content">
          <span>2021 ALPA TECHNOLOGIES</span>
          <span>Mentions Legales</span>
          <span>Fattu in Corsica</span>
        </div>
      </footer>
    </div>

    <!-- SECTION HOME (quand homePage = true) -->
    <div class="home-section" v-else>
      <component
        :is="dynamicComponent"
        :exhibitIdProps="exhibitId"
        :homeImage="homeImage"
        @exit="exitHomePage"
      ></component>
    </div>

    <!-- LIGHTBOX OVERLAY -->
    <div
      v-if="selectedImageIndex !== null"
      class="lightbox"
      @click="closeLightbox"
    >
      <!-- Image dans la lightbox, clic stoppé pour ne pas fermer -->
      <img
        :src="resourceUrls[selectedImageIndex]"
        alt="Full Image"
        class="lightbox-image"
        @click.stop
      />
      <!-- Bouton de fermeture -->
      <button class="lightbox-close" @click.stop="closeLightbox">X</button>
    </div>
  </div>
</template>

<script setup>
/* ---------------------
   IMPORTS
---------------------- */
import { ref, computed, onMounted, defineProps } from 'vue';
import { useRoute } from 'vue-router';
import axios from "axios";

// Import des différents modèles (pour la home)
import Modele_1 from "../../components/Models/Modele_1.vue";
import Modele_2 from "../../components/Models/Modele_2.vue";
import Modele_3 from "../../components/Models/Modele_3.vue";
import Modele_4 from "../../components/Models/Modele_4.vue";
import Modele_5 from "../../components/Models/Modele_5.vue";
import Modele_6 from "../../components/Models/Modele_6.vue";
import Modele_7 from "../../components/Models/Modele_7.vue";

/* ---------------------
   PROPS
---------------------- */
const props = defineProps({
  homePageProps: {
    type: Boolean,
    default: true,
  },
  exhibitId: {
    type: String,
    default: null,
  },
});

/* ---------------------
   STATES (data)
---------------------- */
const route = useRoute();
const id = ref(props.exhibitId ?? route.params.id);

const resources = ref([]);
const name = ref("");
const userName = ref("");
const homeImage = ref({});
const resourceUrls = ref([]);

// Contrôle de la page d'accueil
const homePage = ref(props.homePageProps);

// Modèle sélectionné
const selectedTemplate = ref("");

// Lightbox
const selectedImageIndex = ref(null);

/* ---------------------
   COMPUTED
---------------------- */
/**
 * On utilise la première image comme image de fond
 * pour le header diagonal (si on a une image)
 */
const heroBackgroundStyle = computed(() => {
  if (resourceUrls.value.length > 0) {
    return {
      backgroundImage: `url(${resourceUrls.value[0]})`,
    };
  }
  return {};
});

/* ---------------------
   MODELES DYNAMIQUES
---------------------- */
const modeleComponents = {
  Modele_1,
  Modele_2,
  Modele_3,
  Modele_4,
  Modele_5,
  Modele_6,
  Modele_7
};

const dynamicComponent = computed(() => {
  return modeleComponents[selectedTemplate.value] || null;
});
/* ---------------------
   FONCTIONS (methods)
---------------------- */

// Récupère le nom de l'expo
async function getExhibitName() {
  try {
    const url = `${process.env.VUE_APP_BASEURL}/exhibit/${id.value}`;
    const response = await axios.get(url);
    name.value = response.data.exhibit_name;
  } catch (error) {
    console.error("Error getting exhibit name:", error);
  }
}

// Récupère le nom de l'utilisateur
async function getUserName() {
  try {
    const url = `${process.env.VUE_APP_BASEURL}/user/${id.value}`;
    const response = await axios.get(url);
    userName.value = response.data.name; 
  } catch (error) {
    console.error(error);
  }
}

// Récupère l'image d'accueil
async function getHomeImage() {
  try {
    const exhibitParam = props.exhibitId ?? route.params.id;
    const url = `${process.env.VUE_APP_BASEURL}/exhibit_home/${exhibitParam}`;
    const response = await axios.get(url);
    homeImage.value = response.data[0];
  } catch (error) {
    console.log(error);
  }
}

// Récupère le modèle
async function getModel() {
  try {
    const exhibitParam = props.exhibitId ?? route.params.id;
    const url = `${process.env.VUE_APP_BASEURL}/model/${exhibitParam}`;
    const response = await axios.get(url);
    if (response.data.length > 0) {
      let modelName = response.data[0].model;
      // Correction
      modelName = modelName.replace(/è/g, 'e').replace(/\s+/g, '_');
      selectedTemplate.value = modelName; 
      // Optionnel: 
      // selectedTemplate.value = modeleComponents[modelName];
      // => si vous voulez passer la définition du composant 
      //    plutôt qu'une string
    }
  } catch (error) {
    console.log(error);
  }
}

// Récupère les ressources (métadonnées)
async function getResources() {
  try {
    const exhibitParam = props.exhibitId ?? route.params.id;

    const resourcesUrl = `${process.env.VUE_APP_BASEURL}/resources`;
    const response = await axios.post(resourcesUrl, { exhibitId: exhibitParam });
    const allResources = response.data;

    const galleryUrl = `${process.env.VUE_APP_BASEURL}/galleryResources/${exhibitParam}`;
    const galleryResponse = await axios.get(galleryUrl);
    const galleryResourceIds = galleryResponse.data.map(res => res.resource_id);

    resources.value = allResources.filter(resource =>
      galleryResourceIds.includes(resource.id)
    );
  } catch (error) {
    console.error("Error fetching resources:", error);
  }
}

// Récupère les URLs S3
async function fetchResources() {
  if (resources.value.length === 0) {
    await getResources();
  }
  const urls = resources.value.map(
    (r) => `${process.env.VUE_APP_BASEURL}/s3Resource/${r.name}`
  );
  try {
    const responses = await Promise.all(urls.map(url => axios.get(url)));
    resourceUrls.value = responses.map(r => r.data);
  } catch (error) {
    console.error("Erreur lors de la récupération des URLs", error);
  }
}

// Quitter la page d'accueil
function exitHomePage() {
  homePage.value = false;
}

// Random style pour effet collage
function getRandomTransformStyle(index) {
  const angle = (index * 7) % 15 - 7; 
  const transX = (index * 20) % 60 - 30;
  return {
    transform: `rotate(${angle}deg) translateX(${transX}px)`,
  };
}

// Ouvrir la lightbox
function openLightbox(index) {
  selectedImageIndex.value = index;
}

// Fermer la lightbox
function closeLightbox() {
  selectedImageIndex.value = null;
}

/* ---------------------
   LIFECYCLE
---------------------- */
onMounted(async () => {
  await getHomeImage();
  await getResources();
  await fetchResources();
  await getExhibitName();
  await getUserName();
  await getModel();
});
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Raleway:wght@300;500;700&display=swap");

/* Conteneur global */
.exhibition-wrapper {
  font-family: "Raleway", sans-serif;
  color: #333;
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  overflow: hidden;
}

/* Header diagonal */
.diagonal-header {
  width: 100%;
  height: 60vh;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  position: relative;
  clip-path: polygon(0 0, 100% 0, 100% 70%, 0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}
.header-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0,0,0,0.3);
}
.expo-title {
  position: relative;
  color: #fff;
  font-size: 3rem;
  text-transform: uppercase;
  z-index: 1;
  letter-spacing: 3px;
}

/* Collage */
.collage-section {
  min-height: 50vh;
  padding: 50px 20px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
  background: #fafafa;
}
.collage-item {
  background: #fff;
  width: 280px;
  border-radius: 10px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.5s;
  cursor: pointer;
}
.collage-item:hover {
  transform: rotate(0deg) translateX(0) scale(1.03);
  z-index: 2;
}
.collage-image {
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}
.collage-info {
  padding: 15px;
}
.collage-title {
  margin: 0 0 5px;
  font-weight: 700;
  font-size: 1.1rem;
}
.collage-description {
  font-size: 0.9rem;
  opacity: 0.8;
}

/* Footer incurvé */
.curved-footer {
  position: relative;
  background: #222;
  color: #fff;
  clip-path: polygon(0 0, 100% 10%, 100% 100%, 0 100%);
  margin-top: 3rem;
  min-height: 200px;
  display: flex;
  align-items: flex-end;
}
.footer-content {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 20px 0;
  text-transform: uppercase;
  font-size: 0.8rem;
}

/* Home section */
.home-section {
  width: 100%;
  height: 100vh;
  background: #fff;
  position: relative;
}

/* LIGHTBOX */
.lightbox {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.9);
  z-index: 9999;
  overflow: auto;
  padding: 20px;
  cursor: default;
}

.lightbox-image {
  display: block;
  margin: auto;
  max-width: none;
  width: 130%;
  height: auto;
  transform-origin: center;
}

.lightbox-close {
  position: fixed;
  top: 20px;
  right: 30px;
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid white;
  color: #fff;
  font-size: 2rem;
  cursor: pointer;
  z-index: 10000;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
}

.lightbox-close:hover {
  background: rgba(0, 0, 0, 0.8);
}
</style>
